import { memo, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartConfig,
} from '@parrot-software/design-system';
import type { TransformedChartData } from '@reports/presentation/utils/chartDataTransformers';
import type { PieLabelEntry } from '@reports/presentation/utils/chartLabelRenderers';

export interface ReportPieChartProps {
  chartData: TransformedChartData[];
  colorVariations: string[];
  showTooltip: boolean;
  showLabels: boolean;
  showLegend: boolean;
  tooltipFormatter: (value: any, name: string | number, item: any) => React.ReactElement;
  labelRenderer: (entry: PieLabelEntry) => React.ReactElement;
  chartConfig: ChartConfig;
}

/**
 * ReportPieChart - Pie chart renderer for reports
 *
 * Renders a pie chart using Recharts with optional tooltips and custom labels.
 * Automatically filters out data items with zero values.
 * Memoized to prevent unnecessary re-renders.
 *
 * @param {ReportPieChartProps} props - Component properties
 * @param {TransformedChartData[]} props.chartData - Chart data in Recharts format
 * @param {string[]} props.colorVariations - Array of colors for pie segments
 * @param {boolean} props.showTooltip - Whether to show tooltips on hover
 * @param {boolean} props.showLabels - Whether to show labels outside segments
 * @param {boolean} props.showLegend - Whether to show chart legend
 * @param {Function} props.tooltipFormatter - Function to format tooltip content
 * @param {Function} props.labelRenderer - Function to render custom labels
 * @param {ChartConfig} props.chartConfig - Chart configuration
 * @returns {React.ReactElement} Pie chart component
 *
 * @example
 * <ReportPieChart
 *   chartData={transformedData}
 *   colorVariations={['#F04E4A', '#F26B67', '#F48884']}
 *   showTooltip={true}
 *   showLabels={true}
 *   showLegend={true}
 *   tooltipFormatter={pieTooltipFormatter}
 *   labelRenderer={pieLabelRenderer}
 *   chartConfig={{ segment1: { label: "Category A", color: "#000000" } }}
 * />
 */
const ReportPieChartComponent = ({
  chartData,
  colorVariations,
  showTooltip,
  showLabels,
  showLegend,
  tooltipFormatter,
  labelRenderer,
  chartConfig,
}: ReportPieChartProps) => {
  // Filter out items with zero values and create corresponding color array
  const { filteredChartData, filteredColors } = useMemo(() => {
    const filtered = chartData.filter((item) => item.value !== 0);

    return {
      filteredChartData: filtered,
      filteredColors: filtered.map((_, index) => colorVariations[index] || colorVariations[0]),
    };
  }, [chartData, colorVariations]);

  return (
    <ChartContainer className="h-[275px] w-full" config={chartConfig}>
      <ResponsiveContainer height="100%" width="100%">
        <PieChart>
          {showTooltip && <ChartTooltip content={<ChartTooltipContent formatter={tooltipFormatter} />} />}

          <Pie
            data={filteredChartData}
            dataKey="value"
            label={showLabels ? labelRenderer : false}
            labelLine={showLabels}
            nameKey="name"
          >
            {filteredChartData.map((entry, index) => (
              <Cell fill={filteredColors[index]} key={`cell-${index}`} />
            ))}
          </Pie>
          {showLegend && <ChartLegend className="flex-wrap" content={<ChartLegendContent />} verticalAlign="bottom" />}
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

/**
 * Memoized version of ReportPieChart
 *
 * Prevents re-renders when props haven't changed, improving performance.
 */
export const ReportPieChart = memo(ReportPieChartComponent);
